import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Grid, GridItem, Container, TitleSet, media, Heading, InternalLink } from '../../ui-components';

const propTypes = {
  backgroundColor: PropTypes.string,
  headline: PropTypes.string,
  subHeadline: PropTypes.string,
  headlineText: PropTypes.string,
  gridItems: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      subTitle: PropTypes.string,
      url: PropTypes.string,
      image: PropTypes.any
    })
  ),
  ctaLinkLabel: PropTypes.string,
  ctaLinkUrl: PropTypes.string,
  imageOnly: PropTypes.bool,
  inPaper: PropTypes.bool,
  centerAwardsGrid: PropTypes.bool
};

/**
 * Component that renders a block of service offerings
 *
 * @component SectionContentGrid
 *
 * @param {object} props
 *
 * @prop {string} backgroundColor
 * @prop {string} headline
 * @prop {string} subHeadline
 * @prop {string} headlineText
 * @prop {objecy} gridItems
 * @prop {string} ctaLinkLabel
 * @prop {string} ctaLinkUrl
 * @prop {string} imageOnly
 * @prop {boolean} inPaper
 * @prop {boolean} centerAwardsGrid
 *
 * @return {JSX}
 */
const SectionContentGrid = props => {
  const {
    backgroundColor = 'pureWhite',
    headline,
    subHeadline,
    headlineText,
    gridItems,
    ctaLinkLabel,
    ctaLinkUrl,
    imageOnly,
    removeBottomPadding = false,
    centerAwardsGrid = false
  } = props;

  /**
   * Renders the title
   *
   * @function renderTitleSection
   * @returns {jsx}
   */
  const renderTitleSection = () => {
    return (
      <TitleSection>
        <TitleSet label={subHeadline} title={headline} bodyText={headlineText} />
      </TitleSection>
    );
  };

  /**
   * Renders the grid section
   *
   * @function renderOfferingsSection
   * @returns {jsx}
   */
  const renderGridSection = () => {
    let colWidth;
    if (centerAwardsGrid) {
      colWidth = gridItems.length < 3 ? 12 : 6;
    } else {
      colWidth = gridItems.length === 4 ? 6 : 4;
    }
    let colSize = (centerAwardsGrid && gridItems.length) === 1 ? 12 : 6;

    const gridItemsContent = gridItems.map((gridItem, index) => (
      <StyledGridItem col={colWidth} collg={colSize} colsm={colSize} key={index} centerContent={imageOnly}>
        <GridItemContainer>
          {gridItem.image?.gatsbyImageData && (
            <GatsbyImage
              objectFit={'contain'}
              image={gridItem.image.gatsbyImageData}
              alt={gridItem?.title || 'Grid Image'}
            />
          )}
          {gridItem.title && (
            <Heading label={5} level={5}>
              {gridItem.title}
            </Heading>
          )}
          {gridItem.subTitle && <p>{gridItem.subTitle}</p>}
          {gridItem.url && (
            <InternalLink withArrow to={`${gridItem.url}`}>
              Read On
            </InternalLink>
          )}
        </GridItemContainer>
      </StyledGridItem>
    ));

    return (
      <Grid gridGap={64} noWrap={imageOnly} awardGap={centerAwardsGrid}>
        {gridItemsContent}
      </Grid>
    );
  };

  return (
    <GridContainer backgroundColor={backgroundColor} removeBottomPadding={removeBottomPadding} sectionContainer>
      {headline && renderTitleSection()}
      {gridItems && renderGridSection()}
      {ctaLinkLabel && ctaLinkUrl && (
        <LinkContainer>
          <InternalLink linkstyle="primary-button" to={ctaLinkUrl}>
            {ctaLinkLabel}
          </InternalLink>
        </LinkContainer>
      )}
    </GridContainer>
  );
};

SectionContentGrid.propTypes = propTypes;

export default SectionContentGrid;

const TitleSection = styled.div`
  text-align: center;
`;

const LinkContainer = styled(props => <Container {...props} />)`
  text-align: center;
`;

const GridContainer = styled(Container)`
  background-color: ${props => props.backgroundColor};
  padding-bottom: ${props => (props.removeBottomPadding ? '0' : '')};
`;

const GridItemContainer = styled.div`
  text-align: center;

  img {
    margin: 0;
    max-height: 160px;
  }

  h5 {
    margin-bottom: 12px;
  }

  p {
    font-size: 16px;
    line-height: 28px;
    margin-bottom: 12px;
  }

  @media screen and (max-width: ${media.mediumTop}) {
    padding: 10px;
    margin-bottom: 40px;
  }

  @media screen and (max-width: ${media.mediumBottom}) {
    text-align: center;
  }
`;

const StyledGridItem = styled(props => <GridItem {...props} />)`
  justify-content: center;
  align-items: start;
  a {
    justify-content: center;
  }
`;
